import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import PaymentCardItem from 'components/Payment/PaymentCardItem';
import PaymentCardItemSkeleton from 'components/Payment/PaymentCardItemSkeleton';
import Section from 'components/Section';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { PaymentCard } from 'models/paymentCard';
import PaymentGroupCardSelection from 'screens/components/Payment/PaymentGroupCardSelection';
import { getAllPaymentCards, getGroupAllPaymentCards, getCompanyLineCredit } from 'services/paymentMethodService';
import { htmlT } from 'utils/generic';

interface Props {
  groupId?: string;
  isEdit: boolean;
  showPaymentSelection: boolean;
  paymentCardId?: number;
  onCloseModal: () => void;
  onAdd: () => void;
  onConfirmSelection: (cardId?: number) => void;
}

const GroupPayments = ({
  groupId,
  isEdit,
  showPaymentSelection,
  paymentCardId,
  onCloseModal,
  onAdd,
  onConfirmSelection,
}: Props) => {
  const history = useHistory();
  const toast = useToast();
  const { t } = useStoreTranslation();
  const [groupCard, setGroupCard] = useState<PaymentCard | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState<PaymentCard[]>([]);
  const [cardIdDefault, setCardIdDefault] = useState(0);
  const accountResult = useSelector((state: RootState) => state.user.accountResult);
  const [hasLineCredit, setHasLineCredit] = useState(false);
  const companyId = accountResult?.companyId;

  const getGroupCard = useCallback(
    async (cardId?: number) => {
      setIsLoading(true);
      if (companyId) {
        const {
          data: { lineOfCreditAvailable },
        } = await getCompanyLineCredit(companyId);
        if (lineOfCreditAvailable) {
          setIsLoading(false);
          setHasLineCredit(true);
          return;
        }
      }
      try {
        if (groupId) {
          const response = await getGroupAllPaymentCards(groupId);
          if (response.length !== 0) {
            setGroupCard(response[0]);
            setIsLoading(false);
          } else {
            const cardList = await getAllPaymentCards();
            if (cardList.length !== 0) {
              const selCard = cardList.filter((item) => item.card.default_cc);
              if (selCard && selCard.length > 0) {
                setGroupCard(selCard[0].card);
                setCardIdDefault(selCard[0].card.id);
              }
            }
            setIsLoading(false);
          }
        }
        if (!isEdit) {
          const cardList = await getAllPaymentCards();
          if (cardList.length !== 0) {
            setList(cardList.map((item) => item.card));
            let selCard = cardList.filter((item) => item.card.default_cc);
            if (selCard && selCard.length > 0) {
              setGroupCard(selCard[0].card);
              setCardIdDefault(selCard[0].card.id);
            }
            if (typeof cardId !== 'undefined' && cardId !== 0) {
              selCard = cardList.filter((item) => item.card.id === cardId);
              if (selCard && selCard.length > 0) {
                setGroupCard(selCard[0].card);
              }
            }
          }
          setIsLoading(false);
        }
      } catch (error) {
        toast.add({
          type: 'danger',
          message: error.message,
        });
      }
    },
    [companyId, groupId, isEdit, toast],
  );

  useEffect(() => {
    getGroupCard(paymentCardId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hasLineCredit) {
    return null;
  }
  return (
    <Section title={t('group.payments.title')} details={htmlT(t, 'group.payments.details')}>
      {isEdit ? null : (
        <ApplicationOverlayContent sidebar show={showPaymentSelection} onClose={onCloseModal}>
          <PaymentGroupCardSelection
            showCardSelection={showPaymentSelection}
            cardSelected={groupCard}
            onConfirmSelection={async (cardId?: number) => {
              getGroupCard(cardId);
              if (cardId === cardIdDefault) {
                onConfirmSelection(0);
              } else {
                onConfirmSelection(cardId);
              }
            }}
            onCloseSelectionScreen={onCloseModal}
            cardList={list}
            isLoading={isLoading}
          />
        </ApplicationOverlayContent>
      )}
      {isLoading ? (
        <PaymentCardItemSkeleton />
      ) : (
        <PaymentCardItem
          placeholder={t('group.payments.addPayment')}
          onClick={() => {
            if (groupId) {
              history.push(`/group/${groupId}/payment`);
              return;
            }
            onAdd();
          }}
          card={groupCard}
        />
      )}
    </Section>
  );
};

export default GroupPayments;
